import { camelizeObjectKeys, toSafeErrorLog } from '@utils'
import { axiosClient } from '@services/clients'
import { countries } from '@shared'

export async function getTotalPlayers() {
  try {
    const {
      data: { result },
    } = await axiosClient(`/players/total`)
    return Promise.resolve(camelizeObjectKeys(result))
  } catch (err) {
    console.error(err)
    return Promise.reject(err)
  }
}

export async function getTotalNewPlayers() {
  try {
    const {
      data: { result },
    } = await axiosClient(`/players/total?days=30`)
    return Promise.resolve(camelizeObjectKeys(result))
  } catch (err) {
    console.error(err)
    return Promise.reject(err)
  }
}

export async function getActivePlayers() {
  try {
    const {
      data: { result },
    } = await axiosClient(`/players/active`)
    return Promise.resolve(camelizeObjectKeys(result))
  } catch (err) {
    console.error(err)
    return Promise.reject(err)
  }
}

export async function getActiveTrends() {
  try {
    const {
      data: { result },
    } = await axiosClient(`/players/active/trend`)
    return Promise.resolve(camelizeObjectKeys(result))
  } catch (err) {
    console.error(err)
    return Promise.reject(err)
  }
}

export async function getNewTrends() {
  try {
    const {
      data: { result },
    } = await axiosClient(`/players/new/trend`)
    return Promise.resolve(camelizeObjectKeys(result))
  } catch (err) {
    console.error(err)
    return Promise.reject(err)
  }
}

export async function getTotalTrends() {
  try {
    const {
      data: { result },
    } = await axiosClient(`/players/trend`)
    return Promise.resolve(camelizeObjectKeys(result))
  } catch (err) {
    console.error(err)
    return Promise.reject(err)
  }
}

export async function getTotalByCountry() {
  try {
    const {
      data: { result },
    } = await axiosClient(`/players/total/country`)
    return Promise.resolve(result)
  } catch (err) {
    console.error(err)
    return Promise.reject(err)
  }
}

export async function getTopByCountry() {
  try {
    const {
      data: { result },
    } = await axiosClient(`/players/info/country`)
    return Promise.resolve(camelizeObjectKeys(result))
  } catch (err) {
    console.error(err)
    return Promise.reject(err)
  }
}

export async function getTotalContinent() {
  try {
    const {
      data: { result },
    } = await axiosClient(`/players/total/continent`)
    return Promise.resolve(camelizeObjectKeys(result))
  } catch (err) {
    console.error(err)
    return Promise.reject(err)
  }
}

export async function getTotalNewContinent() {
  try {
    const {
      data: { result },
    } = await axiosClient(`/players/total/continent?days=30`)
    return Promise.resolve(camelizeObjectKeys(result))
  } catch (err) {
    console.error(err)
    return Promise.reject(err)
  }
}

export async function getPlayersOnline() {
  try {
    const {
      data: { result },
    } = await axiosClient(`/players/online`)
    return Promise.resolve(camelizeObjectKeys(result))
  } catch (err) {
    console.error(err)
    return Promise.reject(err)
  }
}

export async function getPlayer(playerId) {
  const {
    data: { result },
  } = await axiosClient(`/players/${playerId}`)

  return result
}

// Map frontend filter keys to backend API parameter names
const FILTER_KEY_TO_PARAM = {
  firstName: 'first_name',
  lastName: 'last_name',
  phoneNumber: 'contact_number',
  email: 'email',
  postCode: 'post_code',
  dob: 'date_of_birth',
  signUpDateStart: 'sign_up_date_start',
  signUpDateEnd: 'sign_up_date_end',
  lastLoginDateStart: 'last_login_start',
  lastLoginDateEnd: 'last_login_end',
  playerId: 'player_id',
  externalPlayerId: 'external_id',
  networkId: 'network_id',
  siteIds: 'site_ids',
}

/**
 * Build query parameter string from filters, pagination, and sort params.
 * Only includes non-empty values. Arrays are comma-joined.
 */
export function buildPlayersQueryParams(
  filters = {},
  page,
  limit,
  sortField,
  sortDirection,
) {
  const params = new URLSearchParams()

  // Add pagination
  if (page) params.append('page', page)
  if (limit) params.append('limit', limit)

  // Add sort params
  if (sortField) params.append('sort_field', sortField)
  if (sortDirection) params.append('sort_direction', sortDirection)

  // Add filter params (only non-empty values)
  Object.entries(filters).forEach(([key, value]) => {
    const paramName = FILTER_KEY_TO_PARAM[key]
    if (!paramName) return

    // Handle arrays (e.g., siteIds) — comma-join
    if (Array.isArray(value)) {
      if (value.length > 0) {
        params.append(paramName, value.join(','))
      }
      return
    }

    // Handle string values — only include non-empty after trimming
    if (typeof value === 'string' && value.trim() !== '') {
      params.append(paramName, value.trim())
    }
  })

  return params.toString()
}

export async function getPlayers(
  filters,
  page,
  limit,
  sortField,
  sortDirection,
) {
  // Backward compatibility: if filters is a string, treat it as a legacy search text
  if (typeof filters === 'string') {
    const params = new URLSearchParams()
    if (filters) params.append('search', filters)
    if (page) params.append('page', page)
    if (limit) params.append('limit', limit)
    const { data } = await axiosClient(`/players?${params.toString()}`)
    return data
  }

  const queryString = buildPlayersQueryParams(
    filters,
    page,
    limit,
    sortField,
    sortDirection,
  )
  const { data } = await axiosClient(`/players?${queryString}`)

  return data
}

export async function getPlayersStatus() {
  const {
    data: { result },
  } = await axiosClient('/players/status')

  return result
}

export async function getPlayerOnline(playerId) {
  const {
    data: { result },
  } = await axiosClient(`/players/${playerId}/online`)
  return result
}

export async function getPlayersStatusById(statusId) {
  const {
    data: { result },
  } = await axiosClient(`players/status/${statusId}`)
  return result
}

export async function getSelfExclusionInformation(networkId) {
  const {
    data: { result },
  } = await axiosClient(`players/network/${networkId}/status/self-exclusion`)
  return result
}

export async function getTimeOutInformation(networkId) {
  const {
    data: { result },
  } = await axiosClient(`players/network/${networkId}/status/timeout`)
  return result
}

export async function UpdatePlayerStatus({ statusObj, status, statusLevel }) {
  const data = {
    player_status_id: statusObj.player_status_id,
    reason: statusObj.reason,
  }
  const endpoint =
    statusLevel === 'network'
      ? `/players/network/${statusObj.networkId}/${status?.network}/status`
      : `/players/${statusObj.playerId}/${status?.player}/status`
  const {
    data: { result },
  } = await axiosClient.put(endpoint, data)
  return {
    ...result,
    data,
  }
}

export async function UpdatePlayerStatusDuration(data) {
  const { statusObj, status } = data

  const dataR = {
    player_status_id: statusObj.player_status_id,
    reason: statusObj.reason,
    date: statusObj.date,
  }
  const {
    data: { result },
  } = await axiosClient.put(
    `/players/network/${statusObj.networkId}/${status?.status?.network}/status`,
    dataR,
  )
  return {
    ...result,
    data,
  }
}

export async function UpdateSelfExclusionDuration(data) {
  const dataR = {
    status_id: data.player_status_id,
    end_date: data.date,
  }
  const {
    data: { result },
  } = await axiosClient.put(
    `/players/network/${data.networkId}/self-exclusion-period`,
    dataR,
  )
  return {
    ...result,
    data,
  }
}

export async function getPlayerChangelog(playerId) {
  const {
    data: { result },
  } = await axiosClient(`/players/changelog/${playerId}`)
  return result
}

export async function getPlayersChangelog(start, end) {
  const {
    data: { result },
  } = await axiosClient('/players/changelog', start, end)

  return result
}

export async function getPlayerVerificationData(playerId) {
  const {
    data: { result },
  } = await axiosClient(`/transunion/logs?playerID=${playerId}`)
  return result
}

export async function updatePlayer(playerId, data) {
  // contact_number and dial_code formatting
  const dialCode =
    countries.find((c) => c.value === data?.country)?.dial_code ?? ''
  const phone = data?.contact_number?.split?.(dialCode)
  const contactNumber = phone[1]
  const formatedData = {
    ...data,
    dial_code: dialCode,
    contact_number: contactNumber,
  }

  const {
    data: { result },
  } = await axiosClient.put(`/players/${playerId}`, formatedData)

  return result
}

export async function updatePartialPlayer(playerId, data) {
  const {
    data: { result },
  } = await axiosClient.patch(`/players/${playerId}`, data)

  return result
}

export async function getPlayersSession(date, players) {
  const {
    data: { result },
  } = await axiosClient(
    `/players/top/session-duration?limit=${players}&days=${date}`,
  )
  return result
}

export async function addNoteForPlayer(data) {
  const form = new FormData()
  data.attachments.forEach((file) => form.append('attachments', file))

  Object.keys(data).forEach((key) => {
    if (key !== 'attachments' && key !== 'newIdNote') {
      form.append(key, data[key])
    }
  })
  const params = data?.networkId ? `?networkId=${data.networkId}` : ''
  const {
    data: { result },
  } = await axiosClient.post(`/players/${data.id}/notes${params}`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return Promise.resolve(result)
}

export async function getNotesPlayer(playerId, notesFilters) {
  try {
    const {
      data: { result },
    } = await axiosClient.get(`/players/${playerId}/notes`, {
      params: notesFilters,
    })

    return Promise.resolve(result)
  } catch (err) {
    console.error(err)
    return Promise.reject(err)
  }
}

export async function getNotesNetwork(networkId, notesFilters) {
  try {
    const {
      data: { result },
    } = await axiosClient.get(`/networks/${networkId}/notes`, {
      params: notesFilters,
    })

    return Promise.resolve(result)
  } catch (err) {
    console.error(err)
    return Promise.reject(err)
  }
}

export async function getNotePlayer(noteId) {
  try {
    const {
      data: { result },
    } = await axiosClient(`/players/notes/${noteId}`)

    return Promise.resolve(result)
  } catch (err) {
    console.error(err)
    return Promise.reject(err)
  }
}

export async function deleteNote(id) {
  const { data } = await axiosClient.delete(`/players/notes/${id}`)
  return data
}

export async function editNote(id, data) {
  const {
    data: { result },
  } = await axiosClient.put(`/players/notes/${id}`, data)
  return Promise.resolve(result)
}

export async function patchNote(id, data) {
  const {
    data: { result },
  } = await axiosClient.patch(`/players/notes/${id}`, data)
  return Promise.resolve(result)
}

export async function getAnalytics(id) {
  const {
    data: { result },
  } = await axiosClient.get(`/players/${id}/analytics`)

  return result
}

export async function deleteAttachment(id) {
  const {
    data: { result },
  } = await axiosClient.delete(`/players/notes/attachment/${id}`)

  return result
}

export async function addAttachment([id, file]) {
  const form = new FormData()
  form.append('attachments', file)

  const {
    data: { result },
  } = await axiosClient.post(`/players/notes/${id}/attachment`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })

  return result
}

export async function getKYCdetails(id) {
  const {
    data: { result },
  } = await axiosClient.get(`/players/${id}/kyc-failure-details`)

  return result
}

export async function retryKYCcheck(id) {
  const {
    data: { result },
  } = await axiosClient.post(`/players/${id}/kyc-check`)

  return result
}

export async function getAccountSummary(summaryFilters) {
  try {
    const {
      data: { result },
    } = await axiosClient.get(`/account-summaries`, {
      params: summaryFilters,
      headers: { 'authorized-party': 'backoffice' },
    })

    return Promise.resolve(result)
  } catch (err) {
    console.error(err)
    return Promise.reject(err)
  }
}

export async function initiateClearStake(playerId) {
  const {
    data: { result },
  } = await axiosClient.post(`/players/${playerId}/kyc-clearstake/initiate`)

  return result
}

export async function cancelClearStake(playerId) {
  const {
    data: { result },
  } = await axiosClient.post(`/players/${playerId}/kyc-clearstake/cancel`)

  return result
}

// Uses the admin route (playerId in the path). The player-facing /me/status
// route can't be used here: the gateway overwrites x-user from the admin's own
// JWT sub, so it would return the admin's case, not the player's.
export async function getClearStakeStatus(playerId) {
  // skipErrorNotification: this is the only polled query in the app, so a
  // transient GET failure must not show the admin an error notification.
  const {
    data: { result },
  } = await axiosClient.get(`/players/${playerId}/kyc-clearstake/status`, {
    skipErrorNotification: true,
  })

  return result
}

// Every ClearStake attempt for the player, shaped like the TransUnion log rows of
// the Verification Data page. skipErrorNotification: these rows are secondary
// on that page, so a failure here must not show the admin an error notification.
export async function getClearStakeLogs(playerId) {
  const {
    data: { result },
  } = await axiosClient.get(`/players/${playerId}/kyc-clearstake/logs`, {
    skipErrorNotification: true,
  })

  return result
}

export async function getPlayerTags(playerId) {
  const { data } = await axiosClient(`/players/${playerId}/tags`)

  return data
}

export async function getTags() {
  const { data } = await axiosClient(`/players/tags`)

  return data
}

export async function updatePlayerTags(playerId, body) {
  const { data } = await axiosClient.post(`/players/${playerId}/tags`, body)

  return data
}

export async function deletePlayerTags(playerId, body) {
  const { data } = await axiosClient.delete(`/players/${playerId}/tags`, {
    data: body,
  })

  return data
}

export async function getProductsPreferences() {
  try {
    const {
      data: { result },
    } = await axiosClient('/product-preferences')
    return Promise.resolve(result)
  } catch (err) {
    console.error(err)
    return Promise.reject(err)
  }
}

export async function getMarketingPreferences(playerId) {
  try {
    const {
      data: { result },
    } = await axiosClient(`/playerMarketPreference/${playerId}`)
    return Promise.resolve(result)
  } catch (err) {
    console.error(err)
    return Promise.reject(err)
  }
}

export async function updateMarketingPreferences(playerId, data) {
  try {
    const {
      data: { result },
    } = await axiosClient.put(`/updatePlayerMarketPreference/${playerId}`, data)

    return Promise.resolve(result)
  } catch (err) {
    console.error(err)

    return Promise.reject(err)
  }
}

export async function updateAllMarketingPreferences(playerId, data) {
  try {
    const {
      data: { result },
    } = await axiosClient.put(
      `/updateAllPlayerMarketPreferences/${playerId}`,
      data,
    )

    return Promise.resolve(result)
  } catch (err) {
    console.error(toSafeErrorLog(err))

    return Promise.reject(err)
  }
}

export async function getPlayerGroups(filters) {
  const { data } = await axiosClient('/playergroup', {
    params: filters,
  })
  return data
}

export async function getPlayerGroup(id) {
  const { data } = await axiosClient(`/playergroup/info?id=${id}`)

  return data
}

export async function createPlayerGroups(data) {
  try {
    const {
      data: { result },
    } = await axiosClient.post('/playergroup/create', data)

    return {
      ...result,
      data,
    }
  } catch (err) {
    console.error(err)

    throw err
  }
}

export async function addPlayersToGroup(data) {
  try {
    const {
      data: { result },
    } = await axiosClient.post('/playergroup/add', data)

    return Promise.resolve({
      ...result,
      data,
    })
  } catch (err) {
    console.error(err)

    return Promise.reject(err)
  }
}

export async function deletePlayerGroup(id) {
  try {
    const {
      data: { result },
    } = await axiosClient.delete(`/playergroup?id=${id}`)

    return Promise.resolve(result)
  } catch (err) {
    console.error(err)
    return Promise.reject(err)
  }
}

export async function sendPasswordResetEmail(playerId) {
  const { data } = await axiosClient.post(
    `/players/${playerId}/reset-password-email`,
  )
  return data
}

export async function getDepositEligibility(playerId, currencyCode) {
  const {
    data: { result },
  } = await axiosClient(
    `/players/${playerId}/deposit-eligibility?currency=${currencyCode}`,
  )
  return result
}
